import {
  faCheckCircle,
  faEnvelope,
  faWarning,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { MenuProps } from 'antd';
import { Divider, Layout, Menu } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';

import { useAppVersion } from '../../../foundation/config/appVersion';
import { useRoutes } from '../../../foundation/config/routes';
import { useAppDispatch } from '../../../store/hooks';
import {
  useGetVariantAssessmentStagesQuery,
  useGetVariantDetailQuery,
} from '../../api/variantApi';
import { selectUser } from '../../authentication/redux/selectors';
import { VARIANT_STAGE_TYPES } from '../../variant_workflow/constants';
import { setWorkflowStep } from '../../variant_workflow/redux';
import { VariantAssessmentStage } from '../../variant_workflow/redux';

const { Sider: AppSider } = Layout;

const SUCCESS_COLOR = '#12bf75';
const WARNING_COLOR = '#ffa244';

type MenuItem = NonNullable<MenuProps['items']>[number];

const getSelectedMenuKey = (pathname: string, hash: string) => {
  if (pathname.startsWith('/samples/')) {
    return '/dashboard';
  }
  if (pathname === '/legal') {
    return `${pathname}${hash}`;
  }

  return pathname;
};

const Sider = () => {
  const dispatch = useAppDispatch();
  const { routes } = useRoutes();

  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(selectUser);

  const [selectedKeys, setSelectedKeys] = useState([
    getSelectedMenuKey(location.pathname, location.hash),
  ]);
  const activeLegalHash = location.hash;
  // Sider is rendered outside the variant-workflow route subtree, so
  // useParams() would not see :vid here - useMatch reads the URL directly.
  const workflowMatch = useMatch('/variant-workflow/:vid/*');
  const vid = workflowMatch?.params.vid;
  // Read the selected variant straight from the RTK Query cache (populated
  // by the workflow loader / page components) rather than a Redux selector.
  const { data: selectedVariant } = useGetVariantDetailQuery(
    { variant_id: vid },
    { skip: !vid },
  );
  const appVersion = useAppVersion();
  // Read stages straight from the query hook's cache rather than a selector
  const { data: stagesData } = useGetVariantAssessmentStagesQuery(
    { variant_id: selectedVariant?.id },
    { skip: !selectedVariant?.id },
  );
  const workflowStages = stagesData ?? [];

  const SUPPORT_EMAIL = 'dnawiz-support@biologis.eu';

  const buildSupportMailto = () => {
    const subject = 'DNAwiz Support Request';
    const userEmail = (user as any)?.email || 'Anonymous user';
    const body = [
      'Hello DNAwiz Support,',
      '',
      'Please describe your issue briefly:',
      '',
      `User: ${userEmail}`,
      `Page: ${window.location.href}`,
      `Version: ${appVersion}`,
    ].join('\n');
    return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const openSupportMail = useCallback(() => {
    const mailto = buildSupportMailto();
    window.location.href = mailto;
  }, [user, location.pathname, appVersion]);

  const menuRenderer = (item: any): MenuItem => ({
    key: `${item.completePath}`,
    icon: item.icon ? <item.icon /> : undefined,
    label: item.name,
  });

  const menuItems = useMemo(() => {
    const items: MenuItem[] = [];

    for (const item of routes) {
      if (!item.hideInMenu) {
        if (!item.isPublic && user) {
          items.push(menuRenderer(item));
        } else if (item.isPublic) {
          items.push(menuRenderer(item));
        }
      }
    }

    return items;
  }, [routes, user]);

  const handleMenuSelect = ({
    key,
    domEvent,
  }: {
    key: string;
    domEvent?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>;
  }) => {
    if (domEvent?.ctrlKey || domEvent?.metaKey) {
      window.open(key, '_blank', 'noopener,noreferrer');
      return;
    }

    if (location.pathname === key) {
      // same route -> bump a hidden state nonce and replace the entry
      navigate(key, { state: { __refresh: Date.now() }, replace: true });
    } else {
      navigate(key);
    }
  };

  const workFlowDivider = useCallback(
    (key: string): MenuItem => ({
      key,
      className: 'layout__sider-menu-wf-divider',
      disabled: true,
      label: (
        <Divider dashed type="vertical" style={{ borderColor: 'black' }} />
      ),
    }),
    [],
  );

  const getVariantStageInformation = (item: VariantAssessmentStage) => {
    let routeKey,
      name = '';

    let stageIcon = faWarning;
    let iconColor = WARNING_COLOR;

    switch (item.stage_type) {
      case VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION: {
        routeKey = VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION;
        name = 'Clinical Observation';
        break;
      }
      case VARIANT_STAGE_TYPES.FREQUENCY_DATA: {
        routeKey = VARIANT_STAGE_TYPES.FREQUENCY_DATA;
        name = 'Frequency Data';
        break;
      }
      case VARIANT_STAGE_TYPES.MOLECULAR_IMPACT: {
        routeKey = VARIANT_STAGE_TYPES.MOLECULAR_IMPACT;
        name = 'Molecular Impact';
        break;
      }
      case VARIANT_STAGE_TYPES.INTERPRETATION: {
        routeKey = VARIANT_STAGE_TYPES.INTERPRETATION;
        name = 'Interpretation';
      }
    }

    switch (item.status) {
      case 'accepted': {
        stageIcon = faCheckCircle;
        iconColor = SUCCESS_COLOR;
        break;
      }
      case 'revised': {
        stageIcon = faWarning;
        iconColor = WARNING_COLOR;
        break;
      }
    }

    return { routeKey, name, stageIcon, iconColor };
  };

  const renderWorkflowStage = (
    item: VariantAssessmentStage,
    menuKey: string,
    index: number,
  ): MenuItem => {
    const { routeKey, name, stageIcon, iconColor } =
      getVariantStageInformation(item);
    return {
      className: 'layout__sider-menu-item-wf',
      key: `${menuKey}/${routeKey}`,
      icon:
        item.status === 'not_started' ? (
          <span className="layout__sider-menu-item-wf-ns-stage"></span>
        ) : (
          <FontAwesomeIcon
            icon={stageIcon}
            style={{
              color: iconColor,
            }}
          />
        ),
      label: name,
    };
  };

  /**
   * This renders the workflow items on menu and set active status based on the
   * current step.
   */
  const workflowMenuItems = useMemo<MenuItem[]>(() => {
    if (!location.pathname.includes('variant-workflow')) {
      return [];
    }

    // Extract variant_id from URL pathname
    const pathParts = location.pathname.split('/');
    const variantIdIndex = pathParts.indexOf('variant-workflow') + 1;
    const variantId =
      variantIdIndex < pathParts.length ? pathParts[variantIdIndex] : null;

    if (!variantId) {
      return [];
    }

    const subMenuKey = `/variant-workflow/${variantId}`;
    const items: MenuItem[] = [
      {
        key: subMenuKey,
        className: `layout__sider-sub-menu-wf${
          location.pathname === subMenuKey
            ? ' layout__sider-sub-menu-wf--selected'
            : ''
        }`,
        label: 'Variant Workflow',
      },
    ];

    workflowStages.forEach((item, index) => {
      items.push(renderWorkflowStage(item, subMenuKey, index));
      if (index !== workflowStages.length - 1) {
        items.push(workFlowDivider(`${subMenuKey}-divider-${index}`));
      }
    });

    return items;
  }, [location.pathname, workflowStages, workFlowDivider]);

  const mainMenuItems = useMemo<MenuItem[]>(() => {
    return [...(menuItems || []), ...(workflowMenuItems || [])];
  }, [menuItems, workflowMenuItems]);

  const bottomMenuItems = useMemo<MenuItem[]>(
    () => [
      {
        key: 'support-email',
        icon: <FontAwesomeIcon icon={faEnvelope} />,
        label: 'Support',
      },
      {
        key: '/legal#legal-notice',
        className: `layout__sider-menu-item-legal${
          activeLegalHash === '#legal-notice'
            ? ' layout__sider-menu-item-legal--active'
            : ''
        }`,
        label: (
          <span style={{ fontSize: '12pt', color: '#7A9DAE' }}>
            Legal notice
          </span>
        ),
      },
      {
        key: '/legal#privacy-policy',
        className: `layout__sider-menu-item-legal${
          activeLegalHash === '#privacy-policy'
            ? ' layout__sider-menu-item-legal--active'
            : ''
        }`,
        label: (
          <span style={{ fontSize: '12pt', color: '#7A9DAE' }}>
            Privacy policy
          </span>
        ),
      },
      {
        key: '/legal#disclaimer',
        className: `layout__sider-menu-item-legal${
          activeLegalHash === '#disclaimer'
            ? ' layout__sider-menu-item-legal--active'
            : ''
        }`,
        label: (
          <span style={{ fontSize: '12pt', color: '#7A9DAE' }}>Disclaimer</span>
        ),
      },
    ],
    [activeLegalHash],
  );

  useEffect(() => {
    setSelectedKeys([getSelectedMenuKey(location.pathname, location.hash)]);

    if (location.pathname.includes('variant-workflow')) {
      // Extract variant_id from URL pathname
      const pathParts = location.pathname.split('/');
      const variantIdIndex = pathParts.indexOf('variant-workflow') + 1;
      const variantId =
        variantIdIndex < pathParts.length ? pathParts[variantIdIndex] : null;

      if (variantId) {
        const subMenuKey = `/variant-workflow/${variantId}/`;
        const path: any = location.pathname.split(subMenuKey)[1];

        if (path && path !== '') {
          dispatch(setWorkflowStep(path));
        } else {
          dispatch(setWorkflowStep(undefined));
        }
      }
    }
  }, [location.pathname, location.hash]);

  return (
    <AppSider width="15%" theme="light" className="layout__sider">
      <Menu
        mode="inline"
        selectedKeys={selectedKeys}
        onClick={handleMenuSelect}
        className="layout__sider-menu"
        items={mainMenuItems}
      />
      <Menu
        mode="inline"
        selectable={false}
        className="layout__sider-menu layout__sider-menu--bottom"
        items={bottomMenuItems}
        onClick={({ key }) => {
          if (key === 'support-email') {
            openSupportMail();
          } else if (key.startsWith('/legal')) {
            navigate(key);
          }
        }}
      />
    </AppSider>
  );
};

export default Sider;
